/*
Copyright 2024 New Vector Ltd.
Copyright 2019-2022 The Matrix.org Foundation C.I.C.
Copyright 2016 OpenMarket Ltd

SPDX-License-Identifier: AGPL-3.0-only OR GPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE files in the repository root for full details.
*/

import { mergeWith } from "lodash";

import { SnakedObject } from "./utils/SnakedObject";
import { type IConfigOptions } from "./IConfigOptions";
import { isObject, objectClone } from "./utils/objects";
import { type DeepReadonly, type Defaultize } from "./@types/common";

// see element-web config.md for docs, or the IConfigOptions interface for dev docs
export const DEFAULTS: DeepReadonly<IConfigOptions> = {
    brand: "Orlime Connect",
    help_url: "https://connect.orlime.ru/help",
    help_encryption_url: "https://connect.orlime.ru/help#encryption",
    help_key_storage_url: "https://connect.orlime.ru/help#encryption5",
    // Orlime Connect single-server MVP mode:
    // keep the web client pinned to one fixed homeserver by default.
    default_server_config: {
        "m.homeserver": {
            base_url: "https://chat.orlime.ru",
            server_name: "chat.orlime.ru",
        },
    },
    // Orlime Connect single-server MVP mode:
    // hide custom homeserver switching unless it is explicitly re-enabled later.
    disable_custom_urls: true,
    // Orlime Connect MVP mode:
    // keep Russian fixed in the product UI until multi-language support returns.
    disable_login_language_selector: true,
    // Orlime Connect branding pass 1:
    // keep top-level product copy configurable from one place for future branding passes.
    branding: {
        app_title: "Orlime Connect",
        welcome_title: "Orlime Connect",
        welcome_subtitle: "Business messaging and collaboration",
        auth_header_logo_url: "themes/element/img/orlime-connect-logo.png",
    },
    // Orlime Connect web MVP mode:
    // suppress most end-to-end encryption onboarding and verification UX on the web client.
    orlime_disable_e2ee_ux: true,
    // Orlime Connect MVP mode:
    // keep the logged-in UI focused on core messaging and hide unsupported surfaces for launch.
    orlime_post_login_cleanup: true,
    integrations_ui_url: "https://scalar.connect.orlime.ru/",
    integrations_rest_url: "https://scalar.connect.orlime.ru/api",
    show_labs_settings: false,
    force_verification: false,

    jitsi: {
        preferred_domain: "meet.connect.orlime.ru",
    },
    element_call: {
        use_exclusively: false,
        brand: "Element Call",
    },

    // @ts-ignore - we deliberately use the camelCase version here so we trigger
    // the fallback behaviour. If we used the snake_case version then we'd break
    // everyone's config which has the camelCase property because our default would
    // be preferred over their config.
    desktopBuilds: {
        available: true,
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        logo: require("../res/img/element-desktop-logo.svg").default,
        url: "https://connect.orlime.ru/get-started",
    },

    feedback: {
        existing_issues_url:
            "https://github.com/vector-im/element-web/issues?q=is%3Aopen+is%3Aissue+sort%3Areactions-%2B1-desc",
        new_issue_url: "https://github.com/vector-im/element-web/issues/new/choose",
    },

    desktop_builds: {
        available: true,
        logo: "vector-icons/1024.png",
        url: "https://connect.orlime.ru/download",
        url_macos: "https://packages.connect.orlime.ru/desktop/install/macos/Element.dmg",
        url_win64: "https://packages.connect.orlime.ru/desktop/install/win32/x64/Element%20Setup.exe",
        url_win64arm: "https://packages.connect.orlime.ru/desktop/install/win32/arm64/Element%20Setup.exe",
        url_linux: "https://connect.orlime.ru/download#linux",
    },
    mobile_builds: {
        ios: "https://apps.apple.com/app/vector/id1083446067",
        android: "https://play.google.com/store/apps/details?id=im.vector.app",
        fdroid: "https://f-droid.org/repository/browse/?fdid=im.vector.app",
    },
};

export type ConfigOptions = Defaultize<IConfigOptions, typeof DEFAULTS>;

function mergeConfig(
    config: DeepReadonly<IConfigOptions>,
    changes: DeepReadonly<Partial<IConfigOptions>>,
): DeepReadonly<IConfigOptions> {
    // return { ...config, ...changes };
    return mergeWith(objectClone(config), changes, (objValue, srcValue) => {
        // Don't merge arrays, prefer values from newer object
        if (Array.isArray(objValue)) {
            return srcValue;
        }

        // Don't allow objects to get nulled out, this will break our types
        if (isObject(objValue) && !isObject(srcValue)) {
            return objValue;
        }
    });
}

type ObjectType<K extends keyof IConfigOptions> = IConfigOptions[K] extends object
    ? SnakedObject<NonNullable<IConfigOptions[K]>>
    : SnakedObject<NonNullable<IConfigOptions[K]>> | null | undefined;

export default class SdkConfig {
    private static instance: DeepReadonly<IConfigOptions>;
    private static fallback: SnakedObject<DeepReadonly<IConfigOptions>>;

    private static setInstance(i: DeepReadonly<IConfigOptions>): void {
        SdkConfig.instance = i;
        SdkConfig.fallback = new SnakedObject(i);

        // For debugging purposes
        window.mxReactSdkConfig = i;
    }

    public static get(): IConfigOptions;
    public static get<K extends keyof IConfigOptions>(key: K, altCaseName?: string): IConfigOptions[K];
    public static get<K extends keyof IConfigOptions = never>(
        key?: K,
        altCaseName?: string,
    ): DeepReadonly<IConfigOptions> | DeepReadonly<IConfigOptions>[K] {
        if (key === undefined) {
            // safe to cast as a fallback - we want to break the runtime contract in this case
            return SdkConfig.instance || <IConfigOptions>{};
        }
        return SdkConfig.fallback.get(key, altCaseName);
    }

    public static getObject<K extends keyof IConfigOptions>(key: K, altCaseName?: string): ObjectType<K> {
        const val = SdkConfig.get(key, altCaseName);
        if (isObject(val)) {
            return new SnakedObject(val);
        }

        // return the same type for sensitive callers (some want `undefined` specifically)
        return (val === undefined ? undefined : null) as ObjectType<K>;
    }

    public static put(cfg: DeepReadonly<ConfigOptions>): void {
        SdkConfig.setInstance(mergeConfig(DEFAULTS, cfg));
    }

    /**
     * Resets the config.
     */
    public static reset(): void {
        SdkConfig.setInstance(mergeConfig(DEFAULTS, {})); // safe to cast - defaults will be applied
    }

    public static add(cfg: Partial<ConfigOptions>): void {
        SdkConfig.put(mergeConfig(SdkConfig.get(), cfg));
    }
}
